import { Cubit } from "blac";
import { addSentryBreadcrumb } from "src/lib/addSentryBreadcrumb";
import reportErrorSentry from "src/lib/reportErrorSentry";
import { TranslationKey } from "src/types/translationKey";

const LOADING_BREADCRUMB_MS = 15000;

/**
 * The overlay blocks the whole screen and only hides when a shared refcount
 * reaches exactly 0, so any unbalanced start leaves a member looking at a
 * spinner forever. This is the backstop that guarantees that never happens,
 * whatever else is missed.
 */
const LOADING_WATCHDOG_MS = 30000;

export interface BlockingLoadingOverlayErrorProps {
  title?: string | null;
  message?: string;
  retry?: () => void;
  onSkip?: () => void;
  skipText?: TranslationKey;
  onCancel?: () => void;
  cancelText?: TranslationKey;
  showIcon?: boolean;
}

export interface BlockingLoadingOverlaySuccessAction {
  text: TranslationKey;
  onClick: () => void;
}

export interface BlockingLoadingOverlaySuccessProps {
  primaryAction?: BlockingLoadingOverlaySuccessAction;
  secondaryAction?: BlockingLoadingOverlaySuccessAction;
  showIcon: boolean;
}

export interface BlockingLoadingOverlayState {
  status: "error" | "loading" | "none" | "success";
  error?: BlockingLoadingOverlayErrorProps;
  success?: BlockingLoadingOverlaySuccessProps;
  bg: "solid" | "transparent" | "branded" | "partner" | "biometric";
  fadeIn: boolean;
  title?: string | TranslationKey;
  message?: string | TranslationKey;
  progress?: number; // 0-100, undefined = no progress bar
  progressIndeterminate?: boolean; // true = spinner only, false = progress bar
  progressLabel?: string; // e.g., "45 MB / 100 MB"
  onCancel?: () => void; // Callback for cancel button during progress
  cancelText?: TranslationKey;
}

export class BlockingLoadingOverlayBloc extends Cubit<BlockingLoadingOverlayState> {
  constructor() {
    super({
      status: "none",
      bg: "branded",
      fadeIn: true
    });
  }
  loadingCounter = 0;
  longLoadingReportTimeout: ReturnType<typeof setTimeout> | null = null;
  watchdogTimeout: ReturnType<typeof setTimeout> | null = null;

  /**
   * Arms the breadcrumb timer and the watchdog. Called on every start, so each
   * new load pushes the deadline out — the watchdog is here to catch a load that
   * never ends, not slow-but-progressing work.
   */
  private readonly startLoadingTimers = (): void => {
    this.clearLoadingTimers();

    this.longLoadingReportTimeout = setTimeout(() => {
      addSentryBreadcrumb(
        "loading.overlay",
        `Loading has been active for over ${LOADING_BREADCRUMB_MS / 1000} seconds`
      );
    }, LOADING_BREADCRUMB_MS);

    this.watchdogTimeout = setTimeout(() => {
      reportErrorSentry(new Error("Blocking loading overlay watchdog fired"), {
        status: this.state.status,
        title: this.state.title,
        loadingCounter: this.loadingCounter
      });
      this.endAllLoadings();
    }, LOADING_WATCHDOG_MS);
  };

  private readonly clearLoadingTimers = (): void => {
    if (this.longLoadingReportTimeout) {
      clearTimeout(this.longLoadingReportTimeout);
      this.longLoadingReportTimeout = null;
    }
    if (this.watchdogTimeout) {
      clearTimeout(this.watchdogTimeout);
      this.watchdogTimeout = null;
    }
  };

  public readonly endLoading = () => {
    this.loadingCounter = Math.max(this.loadingCounter - 1, 0);
    if (this.loadingCounter === 0) {
      this.emit({
        ...this.state,
        status: "none",
        title: "",
        message: "",
        progress: undefined,
        progressIndeterminate: undefined,
        progressLabel: undefined,
        onCancel: undefined
      });

      this.clearLoadingTimers();
    }
  };

  public readonly loadingSuccess = (
    options: {
      title?: string | TranslationKey;
      message?: string | TranslationKey;
      primaryAction?: BlockingLoadingOverlaySuccessAction;
      secondaryAction?: BlockingLoadingOverlaySuccessAction;
      showIcon?: boolean;
      /**
       * Whether the overlay dismisses itself after a short delay. Defaults to
       * true when no action buttons are provided, false otherwise (the user is
       * expected to dismiss it via a button).
       */
      autoDismiss?: boolean;
    } = {}
  ) => {
    const hasActions = Boolean(
      options.primaryAction || options.secondaryAction
    );

    this.emit({
      ...this.state,
      status: "success",
      title: options.title ?? "",
      message: options.message ?? "",
      success: {
        primaryAction: options.primaryAction,
        secondaryAction: options.secondaryAction,
        showIcon: options.showIcon ?? true
      },
      progress: undefined,
      progressIndeterminate: undefined,
      progressLabel: undefined,
      onCancel: undefined
    });

    const autoDismiss = options.autoDismiss ?? !hasActions;
    if (!autoDismiss) {
      this.clearLoadingTimers();
      return;
    }

    let timeout = 1400;
    if (options.title) {
      timeout += 600;
    }
    if (options.message) {
      timeout += 600;
    }

    setTimeout(() => {
      this.endLoading();
    }, timeout);
  };

  public readonly callSuccessPrimaryAction = () => {
    this.endLoading();
    this.state.success?.primaryAction?.onClick();
  };

  public readonly callSuccessSecondaryAction = () => {
    this.endLoading();
    this.state.success?.secondaryAction?.onClick();
  };

  public readonly loadingError = (error?: BlockingLoadingOverlayErrorProps) => {
    this.loadingCounter = Math.max(this.loadingCounter - 1, 0);
    this.emit({
      ...this.state,
      status: "error",
      error,
      title: error?.title ?? "",
      message: error?.message ?? "",
      progress: undefined,
      progressIndeterminate: undefined,
      progressLabel: undefined,
      onCancel: undefined,
      cancelText: error?.cancelText ?? "cancel"
    });
    addSentryBreadcrumb(
      "loading.overlay",
      `Loading error: ${error?.title ?? "No title"}`
    );
    this.loadingCounter = Math.max(this.loadingCounter - 1, 0);
    if (this.loadingCounter === 0) {
      this.clearLoadingTimers();
    }
  };

  public readonly startLoading = (
    options: {
      bg?: BlockingLoadingOverlayState["bg"];
      fadeIn?: boolean;
      title?: string | TranslationKey;
      message?: string | TranslationKey;
    } = {}
  ) => {
    this.loadingCounter++;

    this.emit({
      ...this.state,
      error: undefined,
      success: undefined,
      status: "loading",
      bg: options.bg ?? "branded",
      fadeIn: options.fadeIn ?? true,
      title: options.title ?? "",
      message: options.message ?? "",
      progress: undefined,
      progressIndeterminate: undefined,
      progressLabel: undefined,
      onCancel: undefined
    });

    if (options.title) {
      addSentryBreadcrumb(
        "loading.overlay",
        `Loading started: ${options.title}`
      );
    }

    this.startLoadingTimers();

    return [this.loadingSuccess, this.loadingError];
  };

  public readonly callErrorRetry = () => {
    if (this.state.error?.retry) {
      this.state.error.retry();
    }
  };

  public readonly callOnCancel = () => {
    if (this.state.error?.onCancel) {
      this.state.error.onCancel();
    }
  };

  public readonly callOnSkip = () => {
    if (this.state.error?.onSkip) {
      this.state.error.onSkip();
    }
  };

  public readonly updateProgress = (progress: number, label?: string): void => {
    if (this.state.status === "loading") {
      this.emit({
        ...this.state,
        progress: Math.min(Math.max(progress, 0), 100), // Clamp between 0-100
        progressLabel: label,
        progressIndeterminate: false
      });
    }
  };

  public readonly startLoadingWithProgress = (
    options: {
      bg?: BlockingLoadingOverlayState["bg"];
      fadeIn?: boolean;
      title?: string | TranslationKey;
      message?: string | TranslationKey;
      onCancel?: () => void; // Optional cancel callback
    } = {}
  ) => {
    this.loadingCounter++;

    this.emit({
      ...this.state,
      error: undefined,
      status: "loading",
      bg: options.bg ?? "branded",
      fadeIn: options.fadeIn ?? true,
      title: options.title ?? "",
      message: options.message ?? "",
      progress: 0,
      progressIndeterminate: false,
      progressLabel: undefined,
      onCancel: options.onCancel
    });

    this.startLoadingTimers();

    return [this.loadingSuccess, this.loadingError];
  };

  public readonly callCancel = () => {
    if (this.state.onCancel) {
      this.state.onCancel();
    }
  };

  public readonly endAllLoadings = () => {
    addSentryBreadcrumb("loading.overlay", "Finishing all loading states");
    this.loadingCounter = 0;
    this.clearLoadingTimers();
    this.emit({
      ...this.state,
      status: "none",
      title: "",
      message: ""
    });
  };
}

const BlockingLoadingOverlayController = new BlockingLoadingOverlayBloc();
export default BlockingLoadingOverlayController;
